import { stripTemplateGuideKeys } from '@angular-workspace/feature';
import {
  buildModelMetadataTemplate,
  validateModelMetadataJson,
} from '@angular-workspace/feature';

function downloadTextFile(
  content: string,
  filename: string,
  mimeType: string,
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export function buildTanuhModelMetadataTemplate(): Record<string, unknown> {
  return {
    _fieldGuide: {
      _note:
        'Fill in the fields below only. Title, permission, type, and other catalogue fields are applied automatically by TANUH.',
      shortDescription: 'Brief description (50–200 characters).',
      longDescription: 'Detailed description (50–2000 characters).',
      industry: 'e.g. Healthcare',
      license: 'e.g. MIT, CC-BY 4.0, Other',
      otherLicense: 'Required only when license is Other.',
    },
    shortDescription:
      'Brief description of the model (50–200 characters). Concise overview of what the model does.',
    longDescription:
      'Detailed description of the model (50–2000 characters). Include architecture, training methodology, use cases, and performance notes.',
    industry: 'Healthcare',
    license: 'MIT',
    otherLicense: '',
  };
}

export function downloadTanuhModelMetadataTemplate(
  filename = 'model-metadata-template.json',
): void {
  downloadTextFile(
    JSON.stringify(buildTanuhModelMetadataTemplate(), null, 2),
    filename,
    'application/json',
  );
}

export function validateTanuhModelMetadataFormJson(
  json: unknown,
): { valid: boolean; missingFields: string[]; error?: string } {
  if (!json || typeof json !== 'object') {
    return {
      valid: false,
      missingFields: [],
      error: 'Invalid JSON metadata file.',
    };
  }

  const data = stripTemplateGuideKeys(json as Record<string, unknown>);
  const missingFields: string[] = [];

  if (!isNonEmptyString(data['shortDescription'])) {
    missingFields.push('Short description');
  } else if (String(data['shortDescription']).trim().length < 50) {
    missingFields.push('Short description (minimum 50 characters)');
  }

  if (!isNonEmptyString(data['longDescription'])) {
    missingFields.push('Long description');
  } else if (String(data['longDescription']).trim().length < 50) {
    missingFields.push('Long description (minimum 50 characters)');
  }

  if (!isNonEmptyString(data['industry'])) {
    missingFields.push('Industry');
  }

  if (!isNonEmptyString(data['license'])) {
    missingFields.push('License');
  } else if (String(data['license']).trim() === 'Other' && !isNonEmptyString(data['otherLicense'])) {
    missingFields.push('Other license (required when license is Other)');
  }

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}

export function enrichTanuhModelMetadataFromUpload(
  uploaded: Record<string, unknown>,
  context?: { title?: string; permission?: 'Open' | 'Restricted' },
): Record<string, unknown> {
  const formFields = stripTemplateGuideKeys(uploaded);

  return {
    ...formFields,
    type: ['adex:AiModel'],
    title: context?.title?.trim() ?? String(formFields['title'] ?? '').trim(),
    permission: context?.permission ?? formFields['permission'] ?? 'Open',
    datasetType: formFields['datasetType'] ?? 'Structured',
    fileFormat: formFields['fileFormat'] ?? 'CSV',
    tags: Array.isArray(formFields['tags']) ? formFields['tags'] : [],
  };
}

/** @deprecated Use validateTanuhModelMetadataFormJson */
export function validateTanuhModelMetadataJson(
  json: unknown,
): { valid: boolean; missingFields: string[]; error?: string } {
  const formResult = validateTanuhModelMetadataFormJson(json);
  if (!formResult.valid) {
    return formResult;
  }

  const enriched = enrichTanuhModelMetadataFromUpload(
    stripTemplateGuideKeys(json as Record<string, unknown>),
  );
  return validateModelMetadataJson(enriched);
}
